import { X, AlertTriangle } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'

export function ConfirmModal() {
  const { confirmOptions, hideConfirm } = useUIStore()

  if (!confirmOptions) return null

  const {
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onConfirm,
    variant = 'default',
  } = confirmOptions

  // Single-button mode: caller passed `cancelLabel: null` to render
  // a warning-style acknowledgement (no choice, just an OK). The
  // backdrop and X still dismiss through hideConfirm.
  const hideCancel = cancelLabel === null

  // Confirm-click path must NOT route through hideConfirm(), because
  // hideConfirm in the store also fires onCancel as part of its
  // teardown (see uiStore.ts). That means a caller that passes both
  // onConfirm and onCancel would see BOTH fire on a single Confirm
  // click -- onConfirm's intended action runs, then onCancel runs and
  // typically undoes whatever step/state the action just set up
  // (the price_drift retries in CheckoutModal are exactly this
  // shape). Clear options directly here so only onConfirm runs on
  // the confirm path; backdrop/X/Cancel still go through hideConfirm
  // where onCancel firing is the correct teardown.
  //
  // The teardown is guarded against CHAINED confirms: an `onConfirm`
  // is allowed to raise the next dialog (the checkout ID check answers
  // "yes" and then hits the Lot Number Missing prompt). An unguarded
  // `setState({confirmOptions: null})` here would wipe that freshly-set
  // dialog on its way out, and the cashier would see the click do
  // nothing at all. Comparing identity means we only tear down the
  // dialog we actually rendered -- if `onConfirm` swapped in a new one,
  // it stays up. Non-chaining callers are unaffected.
  const handleConfirm = () => {
    const shown = useUIStore.getState().confirmOptions
    onConfirm()
    if (useUIStore.getState().confirmOptions === shown) {
      useUIStore.setState({ confirmOptions: null })
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={hideConfirm}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {variant === 'danger' && (
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
            )}
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={hideConfirm}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {typeof message === 'string' ? (
            <p className="text-gray-600 whitespace-pre-line">{message}</p>
          ) : (
            message
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          {!hideCancel && (
            <button
              onClick={hideConfirm}
              className="flex-1 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`flex-1 py-3 font-semibold rounded-xl transition-colors cursor-pointer ${
              variant === 'danger'
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
